import React from 'react';
import { Box, Grid } from '@mui/material';
import {
  NameField,
  DescriptionField,
  IconField,
  CategoryField,
  TypeField,
  ApplicationIdsField,
} from '../fields';
import { BuilderTypeValue } from '../fields/TypeField';
import { CampaignConfigSchema } from '../../types';
import { SelectOption } from '../types';
import { applyTypeDefaults } from '../../helpers';

export type HeaderFieldType =
  | 'name'
  | 'description'
  | 'icon'
  | 'category'
  | 'type'
  | 'applicationIds'
  | null;

interface ConfigurationHeaderProps {
  config: CampaignConfigSchema;
  onConfigUpdate: (updates: Partial<CampaignConfigSchema>) => void;
  selectedField: HeaderFieldType;
  onFieldSelect: (field: HeaderFieldType) => void;
  categoriesOptions?: SelectOption[];
  showValidation?: boolean;
  isEditing?: boolean;
}

/**
 * Top configuration block: campaign type, application ids, icon, name,
 * category, description. Type changes apply the registry-driven defaults via
 * helpers/applyTypeDefaults (replacing the old inline handleTypeChange switch).
 */
export const ConfigurationHeader: React.FC<ConfigurationHeaderProps> = ({
  config,
  onConfigUpdate,
  selectedField,
  onFieldSelect,
  categoriesOptions = [],
  showValidation = false,
  isEditing = false,
}) => {
  const handleTypeChange = (value: BuilderTypeValue) => {
    if (value === 'poll') {
      // Poll is a builder-only variant of a message campaign with survey forced on.
      onConfigUpdate({ ...applyTypeDefaults(config, 'message'), isPoll: true });
    } else {
      onConfigUpdate({ ...applyTypeDefaults(config, value), isPoll: false });
    }
    onFieldSelect(null);
  };

  // Show 'poll' in the selector when the isPoll marker is set (config.type is 'message').
  const typeValue: BuilderTypeValue = config.isPoll ? 'poll' : config.type;
  const isCampaignTypeSelected = !!config.isPoll || (config.type && config.type !== 'custom');
  const nameError = showValidation && (!config.name || config.name.trim() === '');
  const descriptionError = showValidation && (!config.description || config.description.trim() === '');

  return (
    <Box p={2} pb={0}>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <TypeField
            value={typeValue}
            onChange={handleTypeChange}
            isSelected={selectedField === 'type'}
            onClick={() => onFieldSelect('type')}
            disabled={isEditing}
          />
        </Grid>
        <Grid item xs={6}>
          <ApplicationIdsField
            values={config.applicationIds || []}
            onChange={values => onConfigUpdate({ applicationIds: values })}
            visible={!!isCampaignTypeSelected}
            required={false}
            isSelected={selectedField === 'applicationIds'}
            onClick={() => onFieldSelect('applicationIds')}
          />
        </Grid>

        <Grid item xs={12}>
          <Box display="flex" gap={1}>
            <Box minWidth={220}>
              <IconField
                value={config.icon}
                onChange={value => onConfigUpdate({ icon: value })}
                isSelected={selectedField === 'icon'}
                onClick={() => onFieldSelect('icon')}
              />
            </Box>
            <Box flex={1}>
              <NameField
                value={config.name}
                onChange={value => onConfigUpdate({ name: value })}
                isSelected={selectedField === 'name'}
                onClick={() => onFieldSelect('name')}
                error={nameError}
                helperText={nameError ? 'Name is required' : ''}
              />
            </Box>
            <Box minWidth={200}>
              <CategoryField
                value={config.category}
                onChange={value => onConfigUpdate({ category: value })}
                isSelected={selectedField === 'category'}
                onClick={() => onFieldSelect('category')}
                options={categoriesOptions}
              />
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} sx={{ mb: '10px' }}>
          <DescriptionField
            value={config.description}
            onChange={value => onConfigUpdate({ description: value })}
            isSelected={selectedField === 'description'}
            onClick={() => onFieldSelect('description')}
            error={descriptionError}
            helperText={descriptionError ? 'Description is required' : ''}
          />
        </Grid>
      </Grid>
    </Box>
  );
};
